import { useTranslation } from 'react-i18next';
import { useEntityOverride } from '@hooks/auth/useEntityOverride';

import type { JSX } from "react";

/**
 * "Viewing as {merchant}" pill rendered by the AppLayout as the first child of
 * `<main>`, pinned flush to the top of the scroll area under the topbar.
 *
 * Visibility mirrors legacy verbatim (`portalnew` `layout/Topbar/index.js:38`):
 *   showEntityBanner = entityOverride && type !== 'admin' && !autoSelected
 *
 * It is intentionally NOT route-gated. The per-segment reset
 * (`useEntityScopeReset`) clears `entityOverride` the moment navigation leaves
 * the segment it was selected on, so a non-admin, non-auto override only ever
 * exists while the user is on its owning scoped segment — the pill therefore
 * appears only there and disappears on leave, with no hard-coded route list to
 * keep in sync. "Continue as Admin" (type `admin`) and a regular
 * merchant/vendor's auto-selected self-scope both show no pill, matching legacy.
 */
export function ViewingAsBar(): JSX.Element | null {
  const { t } = useTranslation('navigation');
  const { entityOverride, clearEntityOverride } = useEntityOverride();

  if (!entityOverride) return null;
  if (entityOverride.type === 'admin') return null;
  if (entityOverride.autoSelected) return null;

  const label = entityOverride.name ?? entityOverride.id ?? entityOverride.type;

  return (
    <div
      className="sticky top-0 z-30 flex justify-center border-b bg-background/95 px-4 py-2 backdrop-blur-sm"
      data-testid="topbar-view-as"
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-foreground px-3 py-1 text-xs text-background shadow-sm">
        <span data-testid="viewing-as-bar-label">{t('viewingAs.label', { label })}</span>
        <button
          type="button"
          onClick={clearEntityOverride}
          className="rounded-full bg-background px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-background/80"
          data-testid="topbar-view-as-cancel"
        >
          {t('viewingAs.cancel')}
        </button>
      </div>
    </div>
  );
}
